import { useLocation } from 'react-router-dom'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { NavLink as NavLinkProps } from 'components/NavigationV3/types'
import { NavLink } from 'components/NavigationV3/components/NavLinks/NavLink'
import {
  Text,
  GreyGrey30,
  GreyGrey60,
  Primary13SemiBold,
  IconLab,
  IconBell,
  IconBank,
  IconStore,
  IconChevronLeft,
  IconCreditCard,
  IconBanknote,
  IconWorld,
  IconKey,
  IconSignature,
  IconReceiptTax,
  IconSignal,
  IconUserGroup,
  IconWallet,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ReactNode } from 'react'
import { usePermissions } from 'lib/rbac'

interface NavLinksProps {
  isCollapsed?: boolean
}

export const SettingsNavLinks = ({ isCollapsed = false }: NavLinksProps) => {
  const { pathname } = useLocation()
  const flags = useFeatureFlags()
  const { can } = usePermissions()

  const links: Record<string, NavLinkProps[]> = {
    account: [
      {
        icon: IconStore,
        label: 'Merchant',
        href: '/settings/merchant',
        permission: 'org.settings:update'
      },
      {
        icon: IconUserGroup,
        label: 'Team',
        href: '/settings/team',
        permission: 'org.settings:invite'
      },
      {
        icon: IconWorld,
        label: 'Customer Portal',
        href: '/settings/portal',
        permission: 'org.settings:update'
      },
      {
        icon: IconBell,
        label: 'Notifications',
        href: '/settings/notifications',
        permission: 'org.settings:update'
      },
      {
        icon: IconSignature,
        label: 'Quotes',
        href: '/settings/quotes',
        enabled: flags.showQuoteBuilder,
        permission: 'org.settings:update'
      }
    ],
    payment: [
      {
        icon: IconWallet,
        label: 'Invoices',
        href: '/settings/invoices',
        permission: 'org.settings:update'
      },
      {
        icon: IconReceiptTax,
        label: 'Sales tax',
        href: '/settings/tax-rates',
        permission: 'org.settings:update'
      },
      {
        icon: IconBanknote,
        label: 'Currencies',
        href: '/settings/currencies',
        permission: 'org.settings:update'
      },
      {
        icon: IconCreditCard,
        label: 'Bank details',
        href: '/settings/bank-details',
        permission: 'org.settings:update'
      },
      {
        icon: IconBank,
        label: 'Revenue recognition',
        href: '/settings/revenue-recognition',
        enabled: flags.useRevRec,
        permission: 'org.revenueRecognition:read'
      }
    ],
    developer: [
      {
        icon: IconKey,
        label: 'API keys',
        href: '/settings/api-keys',
        permission: 'org.settings:update'
      },
      {
        icon: IconSignal,
        label: 'Webhooks',
        href: '/settings/webhooks',
        permission: 'org.settings:update'
      }
    ],
    more: [
      {
        icon: IconLab,
        label: 'Labs',
        href: '/settings/labs',
        'data-testid': 'labs-link',
        // Read (not update) so all roles can discover Labs; enabling features
        // is gated on org.settings:update within the page.
        permission: 'org.settings:read'
      }
    ]
  }

  return (
    <Flex
      direction="column"
      gap="1px"
      px="8px"
      pb={2}
      data-testid="navbar"
      justifyContent={'flex-start'}
      color={GreyGrey60}
    >
      <Box py="8px">
        <NavLink
          icon={IconChevronLeft}
          label="Back to app"
          href="/home"
          pathname={pathname}
          isCollapsed={isCollapsed}
        />
      </Box>

      {Object.entries(links).map(([title, items]) => {
        const filteredItems = items.filter(
          link =>
            ('enabled' in link ? link.enabled : true) &&
            (link.permission ? can(link.permission) : true)
        )

        if (filteredItems.length === 0) {
          return null
        }

        return (
          <NavigationSection
            key={title}
            id={title}
            title={title}
            isCollapsed={isCollapsed}
          >
            {filteredItems.map(link => (
              <NavLink
                key={link.href}
                {...link}
                pathname={pathname}
                isCollapsed={isCollapsed}
              />
            ))}
          </NavigationSection>
        )
      })}
    </Flex>
  )
}

const NavigationSection = ({
  children,
  title,
  id,
  isCollapsed
}: {
  children: ReactNode
  title: string
  id: string
  isCollapsed: boolean
}) => (
  <Flex flexDir="column" py="8px" width="100%" aria-labelledby={id}>
    <Box height="24px" py="4px" position="relative">
      <Box
        width="16px"
        height="1px"
        position="absolute"
        left="8px"
        top="50%"
        bg={GreyGrey30}
        borderRadius="1px"
        opacity={isCollapsed ? 1 : 0}
        transition={`opacity 0.15s ease`}
      />
      <Text
        as="span"
        display="block"
        {...Primary13SemiBold}
        color="inherit"
        fontSize="11px"
        px="8px"
        id={id}
        textTransform="uppercase"
        opacity={isCollapsed ? 0 : 1}
        transform={
          isCollapsed
            ? 'scale(0.6,0.7) translateX(-16px)'
            : 'scale(1,1) translateX(0%)'
        }
        transition="opacity 0.15s ease-out, transform 0.175s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        transformOrigin="center left"
      >
        {title}
      </Text>
    </Box>
    {children}
  </Flex>
)
